import { useEffect, useMemo, useRef, useState } from 'react';

import { useSession } from '@ais-ui/websockets';
import {
  useTrackedMutation as useMutation,
  useTrackedQuery as useQuery,
} from '@ais-ui/performance-tracking';

import { useCredentials } from '@ais-ui/auth';
import {
  useBasePath,
  STALE_TIMES,
  GC_TIMES,
  REFETCH_INTERVALS,
} from '@ais-ui/custom-hooks';
import {
  Configuration,
  HistoricalTrack,
  Ship,
  ShipApi,
  ShipHistoricalPost,
} from '@ais-ui/api-spec/portserver';
import { getBasePath } from './getAISBasePath';
import {
  getAuthHeaderFromCredentials,
  parseDate,
  convertFieldsToDates,
} from '@ais-ui/utils';
import { useApiClientWrapper } from './apiClientWrapper';
import type { PortCallsResponseWithDate } from './types';

// Error parsing utilities
export interface ParsedError {
  type:
    | 'no-data'
    | 'server-error'
    | 'client-error'
    | 'network-error'
    | 'unknown';
  status?: number;
  message: string;
  originalError: Error;
}

// Type guard to check if error is a ResponseError
function isResponseError(
  error: unknown,
): error is { response: { status: number } } {
  return (
    error !== null &&
    typeof error === 'object' &&
    'response' in error &&
    error.response !== null &&
    typeof error.response === 'object' &&
    'status' in error.response &&
    typeof (error.response as Record<string, unknown>).status === 'number'
  );
}

// Parse ResponseError into a cleaner interface
function parseShipApiError(error: Error | null): ParsedError | null {
  if (!error) return null;

  // Check if it's a ResponseError with status code
  if (isResponseError(error)) {
    const status = error.response.status;

    switch (status) {
      case 422:
        return {
          type: 'no-data',
          status,
          message: 'This ship has no recent AIS data available',
          originalError: error,
        };
      case 500:
      case 502:
      case 503:
      case 504:
        return {
          type: 'server-error',
          status,
          message: 'Server error occurred while loading ship data',
          originalError: error,
        };
      case 400:
      case 401:
      case 403:
      case 404:
        return {
          type: 'client-error',
          status,
          message: 'Request error occurred while loading ship data',
          originalError: error,
        };
      default:
        return {
          type: 'unknown',
          status,
          message: 'Error loading ship data',
          originalError: error,
        };
    }
  }

  // Check for network errors
  if (error.message.includes('fetch') || error.message.includes('network')) {
    return {
      type: 'network-error',
      message: 'Network error occurred while loading ship data',
      originalError: error,
    };
  }

  // Default unknown error
  return {
    type: 'unknown',
    message: error.message || 'Error loading ship data',
    originalError: error,
  };
}

export const useShipApi = () => {
  const credentials = useCredentials();
  const { sessionId } = useSession();
  const { protocol, hostname } = useBasePath();

  const apiClientWrapper = useApiClientWrapper();

  let basePath = getBasePath();
  let configuration: Configuration;

  if (hostname.includes('localhost')) {
    const username = credentials?.username || '';
    const password = credentials?.password || '';

    const authHeader = getAuthHeaderFromCredentials(username, password);
    configuration = new Configuration({
      basePath: `${basePath}api/portserver`,
      headers: {
        'X-Application-Name': 'marinepulse',
        Authorization: authHeader,
      },
    });
  } else {
    basePath = `${basePath}aisweb/api/portserver`;
    configuration = new Configuration({
      basePath: basePath,
      headers: {
        'Aisweb-Session': sessionId,
        'X-Application-Name': 'marinepulse',
      },
    });
  }

  const shipApi = useMemo(() => new ShipApi(configuration), [configuration]);

  // The wrapper is used to get error messages from the backend, as they are otherwise thrown out
  return apiClientWrapper(shipApi) as ShipApi;
};

export const usePortServerShipSearch = (search: string) => {
  const shipApi = useShipApi();

  return useQuery(
    {
      queryKey: ['shipSearchGet', search],
      queryFn: async () => {
        return await shipApi.shipSearchQuerystringGet({
          querystring: search,
        });
      },
    },
    'ship:search',
  );
};

export const useShipInfo = (imoOrMmsi: number) => {
  const shipApi = useShipApi();

  return useQuery(
    {
      queryKey: ['shipInfo', imoOrMmsi],
      queryFn: async () => {
        return await shipApi.shipInfoMmsiGet({
          mmsi: imoOrMmsi,
        });
      },
      enabled: !!imoOrMmsi,
      retry: (failureCount, error: any) => {
        // Retry on 429 (Too Many Requests) up to 5 times
        if (error?.response?.status === 429) {
          return failureCount < 5;
        }
        // Retry other errors up to 2 times
        return failureCount < 2;
      },
      retryDelay: (attemptIndex, error: any) => {
        // For 429 errors, use longer exponential backoff
        if (error?.response?.status === 429) {
          return Math.min(1000 * 2 ** attemptIndex, 60000); // Max 60 seconds
        }
        // For other errors, use shorter backoff
        return Math.min(1000 * 2 ** attemptIndex, 30000); // Max 30 seconds
      },
    },
    'ship:info',
  );
};

export const useMonitorShipSelections = (enabled = false) => {
  const shipApi = useShipApi();

  return useQuery(
    {
      queryKey: ['selectShipPolling'],
      queryFn: async () => {
        const data = await shipApi.shipSelectGet();
        return data;
      },
      enabled: enabled,
      refetchInterval: REFETCH_INTERVALS.FAST,
      refetchIntervalInBackground: true,
      staleTime: STALE_TIMES.REALTIME,
      retry: 3,
      retryDelay: 1000,
    },
    'ship:monitorSelections',
  );
};

interface ShipData {
  imo: number;
}

interface UseShipTrackFetchProps {
  shipData?: ShipData;
  beginDate?: Date;
  endDate?: Date;
  resolution: string;
  maxAttempts?: number;
}

export interface UseShipTrackFetchResult {
  data: HistoricalTrack | undefined;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  parsedError: ParsedError | null;
  isNoDataError: boolean;
  isServerError: boolean;
  isClientError: boolean;
  isNetworkError: boolean;
  isSuccess: boolean;
  trackId: string | null;
  refetch: () => void;
}

export const useShipTrackFetch = ({
  shipData,
  beginDate,
  endDate,
  resolution,
  maxAttempts = 30,
}: UseShipTrackFetchProps): UseShipTrackFetchResult => {
  const shipApi = useShipApi();
  const [trackId, setTrackId] = useState<string | null>(null);
  const [completedData, setCompletedData] = useState<HistoricalTrack | null>(
    null,
  );
  const attemptsRef = useRef(0);
  const initiatedRef = useRef(false);
  const hasCompletedRef = useRef(false);

  const trackMutation = useMutation<string, Error, ShipHistoricalPost>(
    {
      mutationFn: async (
        shipHistoricalData: ShipHistoricalPost,
      ): Promise<string> => {
        try {
          const response = await shipApi.shipHistoricalTrackPost({
            json: shipHistoricalData,
          });
          return response.id as string;
        } catch (error) {
          console.error('Error posting ship track request:', error);
          throw error;
        }
      },
      onSuccess: (id) => {
        setTrackId(id);
        attemptsRef.current = 0;
        hasCompletedRef.current = false;
      },
    },
    'ship:trackRequest',
  );

  const trackQuery = useQuery<HistoricalTrack, Error>(
    {
      queryKey: ['shipTrack', trackId],
      queryFn: async (): Promise<HistoricalTrack> => {
        try {
          if (!trackId) throw new Error('No track ID available');

          const response = await shipApi.shipHistoricalTrackIdGet({
            id: trackId,
          });

          // Check if we have actual data (200 response) or empty result (202 response)
          if (!response || !response.ship || !response.track) {
            // Empty or incomplete response means still processing (202)
            attemptsRef.current += 1;
            if (attemptsRef.current >= maxAttempts) {
              throw new Error('Maximum polling attempts reached');
            }
            throw new Error('Still processing');
          }

          // We have complete data (200 response)
          return response;
        } catch (error) {
          console.error('Error fetching ship track:', error);
          throw error instanceof Error ? error : new Error('Unknown error');
        }
      },
      enabled: !!trackId && !hasCompletedRef.current,
      refetchInterval: (query) => {
        // If we have complete data, stop polling
        if (
          query.state.data &&
          query.state.data.ship &&
          query.state.data.track
        ) {
          return false;
        }

        // Otherwise, continue polling every second
        return 1000;
      },
      refetchIntervalInBackground: true,
      retry: (failureCount, error) => {
        // Don't retry on 422 errors (Unprocessable Entity - no data available)
        if (
          error instanceof Error &&
          (error.message.includes('422') ||
            error.message.includes('Unprocessable Entity'))
        ) {
          return false;
        }

        // For all errors (including "Still processing"), limit retries to 3 attempts maximum
        return failureCount < 3;
      },
      // This query polls every second and throws `Error('Still processing')`
      // while the backend renders the track — an expected, transient state
      // that the calling component already presents as loading. Suppress the
      // global error toast so normal polling doesn't surface as
      // "Something went wrong".
      meta: { suppressErrorToast: true },
      staleTime: STALE_TIMES.MODERATE,
      gcTime: GC_TIMES.EXTENDED,
    },
    'ship:trackPoll',
  );

  // Clean up trackId when we have complete data
  useEffect(() => {
    if (
      trackQuery.data?.ship &&
      trackQuery.data?.track &&
      trackId &&
      !hasCompletedRef.current
    ) {
      hasCompletedRef.current = true;
      setCompletedData(trackQuery.data);
      // Delay clearing trackId to ensure the data is properly cached
      const timer = setTimeout(() => {
        setTrackId(null);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [trackQuery.data, trackId]);

  // Reset flags when input parameters change - use stable date representations
  useEffect(() => {
    initiatedRef.current = false;
    hasCompletedRef.current = false;
    setCompletedData(null);
  }, [shipData?.imo, beginDate?.getTime(), endDate?.getTime(), resolution]);

  // Store mutation for explicit calling
  const initiateMutation = useMutation(
    {
      mutationKey: [
        'initiateShipTrack',
        shipData,
        beginDate,
        endDate,
        resolution,
      ],
      mutationFn: async () => {
        if (shipData && beginDate && endDate && resolution) {
          await trackMutation.mutateAsync({
            ship: shipData,
            begin: beginDate.toISOString(),
            end: endDate.toISOString(),
            resolution: resolution,
          });
        }
      },
      onError: (error) => {
        console.error('Failed to initiate ship track:', error);
      },
    },
    'ship:initiateTrack',
  );

  useEffect(() => {
    if (
      shipData &&
      beginDate &&
      endDate &&
      resolution &&
      !initiatedRef.current &&
      !trackId &&
      !hasCompletedRef.current
    ) {
      // Check if we already have cached data for this query
      const cachedData = trackQuery.data;
      if (cachedData?.ship && cachedData?.track) {
        // We already have complete data in cache, no need to fetch again
        hasCompletedRef.current = true;
        return;
      }

      initiatedRef.current = true;
      initiateMutation.mutate();
    }
    // Note: initiateMutation is intentionally excluded from deps to prevent infinite loops
    // The mutation is created fresh each render but we only want to trigger it once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shipData, beginDate, endDate, resolution, trackId, trackQuery.data]);

  const data = completedData || trackQuery.data;
  const hasCompleteData = !!(data?.ship && data?.track);
  const error = (trackMutation.error || trackQuery.error) as Error | null;
  const parsedError = parseShipApiError(error);

  return {
    data,
    isLoading:
      trackMutation.isPending ||
      (trackQuery.isLoading && !hasCompleteData && !hasCompletedRef.current),
    isError: trackMutation.isError || trackQuery.isError,
    error,
    parsedError,
    isNoDataError: parsedError?.type === 'no-data',
    isServerError: parsedError?.type === 'server-error',
    isClientError: parsedError?.type === 'client-error',
    isNetworkError: parsedError?.type === 'network-error',
    isSuccess: hasCompleteData,
    trackId,
    refetch: trackQuery.refetch,
  };
};

export const useShipPortCallsHistory = (
  shipData: Ship | undefined,
  dateRange?: { from?: Date; to?: Date },
) => {
  const shipApi = useShipApi();

  return useQuery(
    {
      queryKey: ['shipPortCalls', shipData, dateRange],
      queryFn: async () => {
        // Use provided date range or default to last 90 days
        let endDate: Date;
        let startDate: Date;

        if (dateRange?.from && dateRange?.to) {
          startDate = dateRange.from;
          endDate = dateRange.to;
        } else {
          endDate = new Date();
          startDate = new Date();
          startDate.setDate(startDate.getDate() - 90);
        }

        const requestBody = {
          json: {
            ship: shipData,
            begin: startDate.toISOString(),
            end: endDate.toISOString(),
          },
        };

        const result = await shipApi.shipHistoricalPortPost(requestBody);

        convertFieldsToDates(result, ['records.ata', 'records.atd']);
        return result as PortCallsResponseWithDate;
      },
      enabled: !!shipData,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      staleTime: STALE_TIMES.IMMUTABLE,
      retry: (failureCount, error: any) => {
        const statusCode =
          error?.response?.parsedBody?.code ||
          error?.response?.status ||
          error?.status;

        // 429 Too Many Requests should be retried
        if (statusCode === 429) {
          return failureCount < 5;
        }

        if (statusCode && statusCode >= 400 && statusCode < 500) {
          return false;
        }

        return failureCount < 3;
      },
      retryDelay: (attemptIndex, error: any) => {
        const statusCode =
          error?.response?.parsedBody?.code ||
          error?.response?.status ||
          error?.status;

        // Back off more aggressively for rate limiting
        if (statusCode === 429) {
          return Math.min(2000 * 2 ** attemptIndex, 30000);
        }

        return Math.min(1000 * 2 ** attemptIndex, 10000);
      },
    },
    'ship:portCalls',
  );
};

interface ShipInfoBulkResult {
  ship?: Ship;
  eta?: Date | null;
  imo?: number;
  mmsi?: number;
  name?: string;
}

export const useBulkShipInfo = (imos: number[]) => {
  const shipApi = useShipApi();

  return useQuery(
    {
      queryKey: ['bulkShipInfo', imos],
      queryFn: async (): Promise<ShipInfoBulkResult[]> => {
        if (!imos || imos.length === 0) {
          return [];
        }

        const data = await shipApi.getBulkShipInfo({
          json: { imo: imos },
        });

        // Map the records to include the ship data at the top level and parse dates
        return (data.records || []).map(
          (record): ShipInfoBulkResult => ({
            ...record.ship,
            eta: parseDate(record.ship?.eta),
            ship: {
              ...record.ship,
            },
          }),
        );
      },
      enabled: imos.length > 0,
      retry: (failureCount) => {
        return failureCount < 5;
      },
      retryDelay: (attemptIndex) => {
        // Exponential backoff: 1s, 2s, 4s, 8s, 16s
        return Math.min(1000 * 2 ** attemptIndex, 16000);
      },
    },
    'ship:bulkInfo',
  );
};
